import * as React from "react"
import { Alert, AlertTitle, Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material"

import {
    MODE_BY_SETUP,
    PRICE_COLUMN_BY_MODE,
    PRICING_CONFIG_MODES,
    ROLE_BY_MODE
} from "../@constants"
import { usePricingConfigDraft } from "../@hooks"
import { customerSetup, modeChangeAcknowledged, pricingConfigMode } from "../@signals"


/** The loud part: switching mode is switching the **user type**, not just a config row.
 *
 * `DIPI_USER.role` is what decides which reference price a user reads (`user` → RRSP,
 * `retailerUser` → the main site of its market), so moving the mode moves what those users see
 * across Pricing and Stock: the `RRSP` column becomes `My Current Price`. Nobody should discover
 * that after saving, hence an explicit acknowledgment before the branch even opens.
 */
export default function ManagementPricingConfigModeChangeWarning() {

    const { discardChanges } = usePricingConfigDraft()

    const setup = customerSetup.value
    const currentMode = MODE_BY_SETUP[setup?.mode]
    const isChanging = !!setup && !!currentMode && currentMode !== pricingConfigMode.value

    if (!isChanging) return <></>

    const targetMode = pricingConfigMode.value
    const isGoingToRetailer = targetMode === PRICING_CONFIG_MODES.RETAILER
    // The users that actually move are the ones holding the *other* role today.
    const affectedUsers = isGoingToRetailer ? setup.standardUsers : setup.retailerUsers

    return <Alert
        severity="error"
        variant="outlined"
        // Restoring the mode is not enough **once the branch has been opened**: the acknowledgment is
        // what opens it, so after ticking it there may be rows staged for a branch that is about to
        // leave the screen — still pending, still counted by the save bar, but impossible to review.
        // Saveable unseen, in other words. Hence the full discard in that case.
        //
        // Without the tick, the other branch was never reachable, so anything staged belongs to the
        // branch being kept: discarding it would throw away work the admin did before touching the
        // selector at all.
        action={<Button
            color="inherit"
            size="small"
            onClick={modeChangeAcknowledged.value
                ? discardChanges
                : () => pricingConfigMode.value = currentMode}
        >
            Keep current
        </Button>}
    >
        <AlertTitle>This changes the user type, not only this screen</AlertTitle>

        <Typography variant="body2">
            The reference price is decided by each user's role (<code>DIPI_USER.role</code>).
            Switching to <strong>{targetMode === PRICING_CONFIG_MODES.RETAILER ? 'Reference retailer' : 'RRSP'}</strong>
            {' '}means turning <code>{ROLE_BY_MODE[currentMode]}</code> into
            {' '}<code>{ROLE_BY_MODE[targetMode]}</code> for this customer.
        </Typography>

        <Box component="ul" sx={{ pl: 3, my: 1 }}>
            <li>
                It applies to the <strong>whole customer</strong>: every enabled user ends up as
                {' '}<code>{ROLE_BY_MODE[targetMode]}</code>, with no exceptions.
            </li>
            <li>
                <strong>{affectedUsers} user{affectedUsers === 1 ? '' : 's'}</strong> would be moved,
                out of {setup.totalUsers} enabled.
                {!!setup.mixed && ' Both roles coexist today, so the ones holding the other one are'
                    + ' dragged along too.'}
            </li>
            <li>
                They stop seeing <strong>{PRICE_COLUMN_BY_MODE[currentMode]}</strong> and start seeing
                {' '}<strong>{PRICE_COLUMN_BY_MODE[targetMode]}</strong> in Pricing and Stock.
            </li>
            {isGoingToRetailer && !setup.mainSites && <li>
                This customer has <strong>no main site configured</strong>, so their reference price
                would be empty until one is set per market below.
            </li>}
            {!isGoingToRetailer && <li>
                Their reference price becomes the RRSP rows below; SKUs without one show no reference.
            </li>}
        </Box>

        <FormControlLabel
            control={<Checkbox
                checked={modeChangeAcknowledged.value}
                onChange={e => modeChangeAcknowledged.value = e.target.checked}
            />}
            label="I understand this changes what those users see in the app"
        />
    </Alert>
}
